import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkle, Coins, Receipt, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "yappy", label: "Yappy" },
  { value: "ach", label: "ACH" },
  { value: "zelle", label: "Zelle" },
  { value: "bank", label: "Bank transfer" },
  { value: "credit", label: "Credit card (off-platform)" },
  { value: "other", label: "Other" },
];

/**
 * Coach-only dialog to grant an athlete a class pack outside Stripe.
 * Captures: which package type, paid vs. on-credit, payment method, reference, optional discount.
 */
export default function GrantPackageDialog({ athleteId, athleteName, trigger, onGranted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    package_type_id: "",
    payment_status: "paid",         // paid | credit (post-paid)
    payment_method: "cash",
    payment_reference: "",
    amount_paid_dollars: "",        // optional override
    note: "",
    effective_date: today,
  });

  useEffect(() => {
    if (open && types.length === 0) {
      api.get("/package-types").then((r) => setTypes(r.data)).catch(() => {});
    }
  }, [open, types.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.package_type_id) { toast.error("Pick a pack"); return; }
    setBusy(true);
    try {
      const body = {
        package_type_id: form.package_type_id,
        payment_status: form.payment_status,
        payment_method: form.payment_status === "paid" ? form.payment_method : null,
        payment_reference: form.payment_reference || null,
        amount_paid_cents: form.amount_paid_dollars
          ? Math.round(Number(form.amount_paid_dollars) * 100)
          : null,
        note: form.note || null,
        effective_date: form.effective_date || null,
      };
      const { data: granted } = await api.post(`/athletes/${athleteId}/packages/manual`, body);
      const retro = granted?.retro_credited || 0;
      const baseToast = form.payment_status === "paid" ? "Pack granted (paid)" : "Pack granted on credit";
      toast.success(retro > 0
        ? `${baseToast} · ${retro} prior check-in${retro === 1 ? "" : "s"} credited`
        : baseToast);
      setOpen(false);
      setForm({
        package_type_id: "", payment_status: "paid", payment_method: "cash",
        payment_reference: "", amount_paid_dollars: "", note: "",
        effective_date: today,
      });
      onGranted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const selectedType = types.find((t) => t.id === form.package_type_id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-terracotta" /> Grant pack
            {athleteName && <span className="text-ink-3 font-normal text-sm">· {athleteName}</span>}
          </DialogTitle>
          <DialogDescription>
            Manually credit a class pack — for cash, check, Yappy, ACH, comp, or any payment outside Stripe.
            Select <strong>On credit</strong> for an athlete training before they've paid.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="grant-package-form">
          <div>
            <Label className="hs-label">Class pack</Label>
            <Select value={form.package_type_id} onValueChange={(v) => setForm({ ...form, package_type_id: v })}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="grant-pkg-type">
                <SelectValue placeholder="Pick a pack…" />
              </SelectTrigger>
              <SelectContent>
                {types.length === 0 && <div className="px-3 py-2 text-sm text-ink-3">No packs configured yet.</div>}
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id} disabled={!t.active}>
                    {t.name} · {t.sessions} sessions · {(t.price_cents / 100).toLocaleString(undefined, { style: "currency", currency: (t.currency || "usd").toUpperCase() })}
                    {!t.active && " (inactive)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="hs-label">Effective date</Label>
            <Input
              type="date"
              value={form.effective_date}
              onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
              className="mt-2 h-11 rounded-xl font-data"
              data-testid="grant-effective-date"
            />
            {form.effective_date && form.effective_date < today ? (
              <p className="text-[11px] text-terracotta mt-1" data-testid="grant-retro-hint">
                Backdated — any uncovered check-ins on or after this date will be credited to this pack.
              </p>
            ) : form.effective_date && form.effective_date > today ? (
              <p className="text-[11px] text-moss mt-1" data-testid="grant-future-hint">
                Granted in advance — the pack activates on this date.
              </p>
            ) : (
              <p className="text-[11px] text-ink-3 mt-1">Defaults to today. Pick an earlier date to credit prior check-ins, or a future date to pre-grant.</p>
            )}
          </div>

          <div>
            <Label className="hs-label">Payment status</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, payment_status: "paid" })}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${
                  form.payment_status === "paid"
                    ? "border-moss bg-sage/40"
                    : "border-line hover:border-line/80"
                }`}
                data-testid="grant-status-paid"
              >
                <div className="flex items-center gap-1.5">
                  <Receipt size={16} className="text-moss" weight="fill" />
                  <span className="font-semibold text-sm">Prepaid</span>
                </div>
                <p className="text-[11px] text-ink-3 mt-1">Athlete has already paid for this pack.</p>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, payment_status: "credit" })}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${
                  form.payment_status === "credit"
                    ? "border-warning bg-warning/10"
                    : "border-line hover:border-line/80"
                }`}
                data-testid="grant-status-credit"
              >
                <div className="flex items-center gap-1.5">
                  <Coins size={16} className="text-warning" weight="fill" />
                  <span className="font-semibold text-sm">On credit</span>
                </div>
                <p className="text-[11px] text-ink-3 mt-1">Post-paid IOU — collect later.</p>
              </button>
            </div>
          </div>

          {form.payment_status === "paid" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="hs-label">Payment method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="grant-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="hs-label">Reference (optional)</Label>
                <Input
                  value={form.payment_reference}
                  onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                  className="mt-2 h-11 rounded-xl"
                  placeholder="check #, txn id…"
                  data-testid="grant-reference"
                />
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-warning/10 border border-warning/30 p-3 flex items-start gap-2">
              <Warning size={18} weight="fill" className="text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-ink-2">
                <strong>On credit</strong> — credits are usable immediately; you'll be reminded to collect.
                Mark paid later by clicking <em>Mark paid</em> on the athlete's billing page.
              </div>
            </div>
          )}

          <div>
            <Label className="hs-label">
              Amount {form.payment_status === "paid" ? "received" : "owed"} (optional override)
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.amount_paid_dollars}
              onChange={(e) => setForm({ ...form, amount_paid_dollars: e.target.value })}
              className="mt-2 h-11 rounded-xl font-data"
              placeholder={selectedType ? `default $${(selectedType.price_cents / 100).toFixed(2)}` : "$0.00"}
              data-testid="grant-amount"
            />
            <p className="text-[11px] text-ink-3 mt-1">Leave blank to use the pack's list price.</p>
          </div>

          <div>
            <Label className="hs-label">Note (optional)</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-2 rounded-xl"
              placeholder="Trial pack, sliding-scale rate, comp from referral…"
              data-testid="grant-note"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="grant-submit">
              {busy ? "Granting…" : "Grant pack"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
